import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../../../../common/hooks/hooks";
import {
  setCurrentStep,
  setAppointmentDate,
  setAppointmentTime,
} from "../../../checkoutSlice";
import {
  selectAppointmentDate,
  selectAppointmentTime,
} from "../../../checkoutSelectors";
import { useLazyGetAvailableSlotsQuery } from "../../../api/appointmentApi";
import { useGetCartQuery } from "../../../../cart/api/cartApi";
import {
  createAppointmentSchema,
  AppointmentFormData,
} from "../../../helpers/checkoutValidation";
import CheckoutStepLayout from "../../common/CheckoutStepLayout";
import Skeleton from "react-loading-skeleton";

const AppointmentStep: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const savedDate = useAppSelector(selectAppointmentDate);
  const savedTime = useAppSelector(selectAppointmentTime);

  const { data: cartData } = useGetCartQuery();

  const [triggerGetSlots, { data: slotsData, isFetching }] =
    useLazyGetAvailableSlotsQuery();

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<AppointmentFormData>({
    resolver: zodResolver(createAppointmentSchema(t)),
    defaultValues: {
      date: savedDate,
      time: savedTime,
    },
  });

  const watchDate = watch("date");
  const watchTime = watch("time");

  const today = new Date().toISOString().split("T")[0];

  useEffect(() => {
    if (watchDate) {
      if (watchDate !== savedDate) {
        setValue("time", "");
      }
      triggerGetSlots(watchDate);
    }
  }, [watchDate, triggerGetSlots, setValue, savedDate]);

  const onSubmit = (data: AppointmentFormData) => {
    dispatch(setAppointmentDate(data.date));
    dispatch(setAppointmentTime(data.time));
    dispatch(setCurrentStep("payment"));
  };

  const onPrevClick = () => {
    if (cartData?.needsShipping) {
      dispatch(setCurrentStep("shipping"));
    } else {
      dispatch(setCurrentStep("billing"));
    }
  };

  return (
    <CheckoutStepLayout
      title={t("appointmentStep.stepTitle", "Randevu Seçimi")}
      onSubmit={handleSubmit(onSubmit)}
      isProcessing={false}
      nextButtonText={t("checkoutForm.actions.continue", "Continue to Payment")}
      showPrevButton={true}
      onPrevious={onPrevClick}
    >
      <div className="checkout-form__section">
        <p
          style={{ marginBottom: "15px", color: "var(--color-text-secondary)" }}
        >
          {t(
            "appointmentStep.description",
            "Sepetinizdeki ürünler için hizmet alacağınız gün ve saati seçiniz.",
          )}
        </p>

        <div className="form-field">
          <label htmlFor="appointment_date" className="required">
            {t("appointmentStep.dateLabel", "Tarih")}
          </label>
          <input
            type="date"
            id="appointment_date"
            min={today}
            {...register("date")}
            className={errors.date ? "has-error" : ""}
          />
          {errors.date && (
            <p className="form-field__error">{errors.date.message}</p>
          )}
        </div>

        {watchDate && (
          <div className="form-field" style={{ marginTop: "20px" }}>
            <label className="required">
              {t("appointmentStep.timeLabel", "Müsait Saatler")}
            </label>

            {isFetching ? (
              <Skeleton height={40} count={2} />
            ) : slotsData?.is_closed ? (
              <div
                className="alert alert--warning"
                style={{
                  padding: "10px",
                  backgroundColor: "#fff3cd",
                  color: "#856404",
                  borderRadius: "4px",
                }}
              >
                {slotsData.message}
              </div>
            ) : slotsData?.slots && slotsData.slots.length > 0 ? (
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "10px",
                  marginTop: "10px",
                }}
              >
                {slotsData.slots.map((slot) => (
                  <label
                    key={slot.time}
                    style={{
                      padding: "10px 15px",
                      border: "1px solid",
                      borderColor:
                        watchTime === slot.time
                          ? "var(--color-primary)"
                          : "var(--color-border)",
                      backgroundColor:
                        watchTime === slot.time
                          ? "color-mix(in srgb, var(--color-primary) 10%, transparent)"
                          : "#fff",
                      color:
                        watchTime === slot.time
                          ? "var(--color-primary)"
                          : "var(--color-text-primary)",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontWeight: watchTime === slot.time ? "bold" : "normal",
                      transition: "all 0.2s ease",
                    }}
                  >
                    <input
                      type="radio"
                      value={slot.time}
                      {...register("time")}
                      style={{ display: "none" }}
                    />
                    {slot.time}
                  </label>
                ))}
              </div>
            ) : (
              <div
                className="alert alert--warning"
                style={{
                  padding: "10px",
                  backgroundColor: "#f8d7da",
                  color: "#721c24",
                  borderRadius: "4px",
                }}
              >
                {t(
                  "appointmentStep.noTimeSlots",
                  "Seçtiğiniz tarihte uygun randevu saati bulunmamaktadır. Lütfen başka bir tarih seçiniz.",
                )}
              </div>
            )}

            {errors.time && (
              <p className="form-field__error">{errors.time.message}</p>
            )}
          </div>
        )}
      </div>
    </CheckoutStepLayout>
  );
};

export default AppointmentStep;
